import { Tooltip } from '@zoom/components';
import classnames from 'classnames';
import { isSameDay } from 'date-fns';
import { remove } from 'ramda';
import React from 'react';
import DatePickerComponent from 'react-datepicker';

import { DatePickerHeader } from './DatePickerHeader';
import { CustomHeaderParams } from './types';
import { useAuth } from '../../app/AuthorizationProvider';

interface MultipleDaysProps {
  daysWithTooltip?: Date[];
  excludedDates?: Date[];
  maxDate?: Date;
  minDate?: Date;
  openToDate?: Date;
  selectedDates: Date[] | undefined;
  setSelectedDates: (selectedDates: Date[]) => void;
  tooltipMessage?: React.ReactNode;
}

export const MultipleDays: React.FC<MultipleDaysProps> = ({
  daysWithTooltip = [],
  excludedDates,
  maxDate,
  minDate,
  openToDate,
  selectedDates,
  setSelectedDates,
  tooltipMessage,
}) => {
  const { localeObj } = useAuth();

  const handleDateSelection = (selectedDate: Date) => {
    if (selectedDates) {
      const selectedDayIndex = selectedDates.findIndex((date) => isSameDay(date, selectedDate));
      if (selectedDayIndex !== -1) {
        setSelectedDates(remove(selectedDayIndex, 1, selectedDates));
      } else {
        setSelectedDates([...selectedDates, selectedDate]);
      }
    } else {
      setSelectedDates([selectedDate]);
    }
  };

  const highlightDates = [] as Array<Record<string, Date[]>>;

  if (daysWithTooltip.length) {
    highlightDates.push({ 'react-datepicker__day--day-with-tooltip': daysWithTooltip });
  }

  if (selectedDates?.length) {
    highlightDates.push({ 'react-datepicker__day--selected': selectedDates });
  }

  const renderDayContents = (dayOfMonth: number, selectedDate: Date) => {
    const isSelected = selectedDates && selectedDates.find((date) => isSameDay(date, selectedDate));
    const content = (
      <span className={classnames('multiple-day', { 'multiple-day--selected': isSelected })}>{dayOfMonth}</span>
    );

    if (
      daysWithTooltip.length &&
      tooltipMessage &&
      daysWithTooltip.filter((day) => day.getTime() === selectedDate.getTime()).length
    ) {
      return (
        <Tooltip placement="bottom" message={tooltipMessage as JSX.Element}>
          {content}
        </Tooltip>
      );
    }

    return content;
  };

  const renderCustomHeader = (params: CustomHeaderParams) => (
    <DatePickerHeader {...params} minDate={minDate} maxDate={maxDate} />
  );

  return (
    <DatePickerComponent
      calendarClassName="date-picker date-picker--multiple-days date-picker__calendar-wrapper date-picker__popper-wrapper"
      excludeDates={excludedDates}
      formatWeekDay={(formattedDate: string) => formattedDate.substring(0, 3)}
      highlightDates={highlightDates.length ? highlightDates : undefined}
      inline
      maxDate={maxDate}
      minDate={minDate}
      onChange={(date) => date && handleDateSelection(date)}
      openToDate={openToDate}
      renderCustomHeader={renderCustomHeader}
      renderDayContents={renderDayContents}
      showMonthDropdown
      showYearDropdown
      locale={localeObj}
    />
  );
};
